import React from "react";

import PropTypes from "prop-types";
import { Trans } from "react-i18next";

import useClickOutside from "../../utils/CustomHooks/useClickOutside";
import MainModalHeader from "../Headers/MainModalHeader";
import LoadingOverlay from "../Loaders/LoadingOverlay";
import { t } from "i18next";

const BaseModal = ({
    id = "base",
    isVisible,
    setIsVisible,
    isSubmitting,
    title,
    titleIcon,
    children,
    action,
    isSmall,
    isMedium,
    isLoading,
    goBackAction,
    hideFooter = false,
    contentStyles = {},
    footerHorizontalLine = true,
}) => {
    const modalRef = useClickOutside(() => closeModal());

    const closeModal = () => {
        setIsVisible && setIsVisible(false);
        action?.cancelAction && action.cancelAction();
    };

    const renderButton = () => {
        const name = typeof action?.name === "string" ? action.name : null;
        if (isSubmitting) {
            return (
                <>
                    <span className="animate-spin mr-3 inline-flex">
                        <i className="fas fa-sync"></i>
                    </span>
                    <Trans i18nKey="requesting">Requesting...</Trans>
                </>
            );
        } else {
            return name || <Trans i18nKey="submit">Submit</Trans>;
        }
    };

    const getId = () => {
        if (typeof title === "string")
            return title?.toLowerCase()?.replaceAll(" ", "-");
        return id;
    };

    return (
        <>
            <div
                className={`modal small-scroll-y ${isVisible ? "show" : ""}`}
                id={`${getId()}-modal`}
            >
                <div
                    ref={modalRef}
                    className={`modal-content-wrapper lg-modal shadow ${
                        isLoading ? "loading" : ""
                    }`}
                >
                    <MainModalHeader
                        header={title}
                        icon={titleIcon}
                        goBackAction={goBackAction}
                        closeModalHandler={closeModal}
                    />
                    <LoadingOverlay active={isLoading} className="radius-less">
                        <div
                            style={contentStyles}
                            className={`modal-content small-scroll-y overflow-auto ${
                                !isSmall ? "h-with-scroll" : ""
                            } ${isMedium ? "h-medium-with-scroll" : ""}`}
                        >
                            {children}
                        </div>
                    </LoadingOverlay>

                    {!hideFooter && (
                        <div
                            className={`${
                                footerHorizontalLine ? "base" : ""
                            } modal-footer`}
                        >
                            <div
                                className={`modal-btn-actions-area flex items-center ${
                                    goBackAction
                                        ? "justify-between"
                                        : "justify-end"
                                }`}
                            >
                                {goBackAction && (
                                    <button
                                        className="default-transparent-btn self-start"
                                        onClick={goBackAction}
                                    >
                                        <i className="fas fa-caret-left"></i>
                                        <span className="ml-2">
                                            {t("goBack")}
                                        </span>
                                    </button>
                                )}
                                <button
                                    onClick={closeModal}
                                    disabled={isSubmitting}
                                    className={`default-transparent-btn modal-btn-close mr-2 ${
                                        isSubmitting ? "disabled" : ""
                                    }`}
                                    data-modal-close="create-access-modal"
                                >
                                    <Trans i18nKey="cancel">Cancel</Trans>
                                </button>
                                {action && (
                                    <button
                                        disabled={
                                            isSubmitting || action?.disabled
                                        }
                                        onClick={action?.submitAction}
                                        className={`default-orange-outline-btn ${
                                            action?.disabled || isSubmitting
                                                ? "disabled"
                                                : ""
                                        }`}
                                        id={`${getId()}-modal-submit-btn`}
                                    >
                                        {renderButton()}
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

BaseModal.propTypes = {
    isVisible: PropTypes.bool.isRequired,
    setIsVisible: PropTypes.func.isRequired,
    isSubmitting: PropTypes.bool,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
    titleIcon: PropTypes.string,
    children: PropTypes.node.isRequired,
    goBackAction: PropTypes.func,
    action: PropTypes.shape({
        name: PropTypes.string,
        disabled: PropTypes.bool,
        submitAction: PropTypes.func,
    }),
    contentStyles: PropTypes.object,
    isLoading: PropTypes.bool,
    isSmall: PropTypes.bool,
    isMedium: PropTypes.bool,
    footerHorizontalLine: PropTypes.bool,
    hideFooter: PropTypes.bool,
};

export default BaseModal;
